import cuid from "cuid";
import { find, get, isString, max } from "lodash";
import moment from "moment";
import omitDeep from "omit-deep-lodash";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { BUILD_GENERAL_COST } from "utils/api/graphql/mutations/costs";
import { INSURANCE_PLAN } from "utils/api/graphql/queries/insurance-plans";
import { SUBSCRIPTION } from "utils/api/graphql/queries/subscription";
import {
  ACTION_TYPES,
  CASE_TYPES,
  DATE_FORMAT,
  INSURANCE_PLAN_PARAMS_RULES,
  KEYS,
  PROCESS_TYPES,
  SCREENS,
  TEMPLATE_TYPES,
  TEMPLATES,
} from "utils/constants";
import { filterByConditions } from "../../../..";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

function findRules({ params, ruleName }) {
  let results = [];

  params.forEach((plan) => {
    if (plan.rules && Array.isArray(plan.rules)) {
      plan.rules.forEach((rule) => {
        if (rule.name === ruleName) {
          results.push(rule.value);
        }
      });
    }
  });

  return results;
}

const EffectiveDate = ({
  project,
  onNext,
  onBack,
  currentStatus,
  tracer,
  updateUrl,
}) => {
  const subscriptionId =
    localStorage.getItem(KEYS.SUBSCRIPTION_ID) ||
    get(project, "fields.currentSubscription");

  const [initialValues, setInitialValues] = useState(null);

  const navigate = useNavigate();

  const insurancePlanId = useMemo(() => {
    return (
      get(
        get(project, "subscriptions", []).find(
          ({ id }) => id === subscriptionId,
        ),
        "insurancePlan.id",
      ) || get(project, "fields.lastSelected.0")
    );
  }, []);

  const DEFAULT_DELTA_START = 1;
  const DEFAULT_DELTA_START_RIA = 40;
  const DEFAULT_DELTA_END_RIA = 0;
  const DEFAULT_DELTA_END = 0;
  const [loading, setLoading] = useState(false);
  const [deltas, setDeltas] = useState({
    start: { normal: DEFAULT_DELTA_START, ria: DEFAULT_DELTA_START_RIA },
    end: { normal: DEFAULT_DELTA_END, ria: DEFAULT_DELTA_END_RIA },
  });

  const findStatus = (data, template) => {
    return find(data, ({ template: _ }) => _ === template);
  };

  const { data } = useQuery(SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
  });

  const [updateProject] = useMutation(graphql.mutations.UPDATE_PROJECT);

  const [buildGeneralCost] = useMutation(BUILD_GENERAL_COST, {
    variables: {
      data: {
        project: { id: project.id },
        insurancePlan: {
          id: get(data, "subscription.insurancePlan.id"),
        },
      },
    },
  });

  const updateProjectStatus = ({ template }) => {
    const status = findStatus(
      get(tracer, "flow.process.processStatuses", []),
      template,
    );
    const actionsToExecute = filterByConditions(
      get(status, "actions", []),
      project,
    );
    const syncActions = actionsToExecute
      .filter(({ type }) => type === ACTION_TYPES.SYNC_PROCESSES)
      .map(({ args: { status } }) => ({
        id: cuid(),
        status: { id: status },
      }));
    updateProject({
      variables: {
        where: { id: project.id },
        data: {
          statuses: [{ id: cuid(), status: { id: status.id } }, ...syncActions],
        },
      },
      onCompleted: ({ updateProject }) => {
        navigate(
          `${SCREENS.PRIVATE.GUEST.PROJECTS.DETAIL.path}/${updateProject?.id}`,
          {
            state: {
              process: get(omitDeep(tracer, "__typename"), "flow.process"),
              tracer: omitDeep(tracer, "__typename"),
            },
          },
        );
      },
    });
  };

  const onProductEdit = () => {
    // onBack();
    // const {
    //   insurancePlans,
    //   currentSubscription,
    //   nextQuoteStatus,
    //   selectedToCompare,
    //   ...fields
    // } = project?.fields;

    // duplicateProjectStatus({
    //   Template:
    //     TEMPLATES[PROCESS_TYPES.B2C].THEMES[TEMPLATE_TYPES.RADIANCE_COSA].PRODUCTS,
    //   prevTemplate:
    //     TEMPLATES[PROCESS_TYPES.B2C].THEMES[TEMPLATE_TYPES.RADIANCE_COSA]
    //       .ADHERENT_TELECOMS,
    //   fields,
    // });
    updateProjectStatus({
      template:
        TEMPLATES[PROCESS_TYPES.B2C].THEMES[TEMPLATE_TYPES.B2C_MUTUAL_MOBILITY]
          .PRODUCTS,
    });
  };

  const recalculateCost2 = ({
    cost,
    issuanceDate,
    oldIssuanceDate,
    setFieldValue,
    setShowChangeCostModal,
    isCancel,
    isRia,
  }) => {
    const formattedIssuanceDate = isString(issuanceDate)
      ? moment(issuanceDate)
      : issuanceDate;

    const formattedOldIssuanceDate = isString(oldIssuanceDate)
      ? moment(oldIssuanceDate)
      : oldIssuanceDate;

    if (formattedIssuanceDate.isSame(formattedOldIssuanceDate.format(), "day"))
      return;
    updateProject({
      variables: {
        where: { id: project.id },
        data: {
          contract: {
            id: get(project, "contract.id"),
            issuanceDate,
          },
        },
      },
      onCompleted: () => {
        buildGeneralCost({
          onCompleted: ({ buildGeneralCost: generalCost }) => {
            if (!generalCost?.cost) {
              setFieldValue("project.contract.issuanceDate", oldIssuanceDate);
              setShowChangeCostModal({
                noCost: true,
              });
              return updateProject({
                variables: {
                  where: { id: project.id },
                  data: {
                    contract: {
                      id: get(project, "contract.id"),
                      issuanceDate: oldIssuanceDate,
                    },
                  },
                },
                onCompleted: () => {
                  buildGeneralCost();
                },
              });
            }
            if (cost === generalCost.cost) return;
            if (isCancel) {
              setFieldValue("project.contract.issuanceDate", issuanceDate);
              refetch();
              return setShowChangeCostModal({});
            }
            setShowChangeCostModal({
              isRia,
              issuanceDate: {
                old: formattedOldIssuanceDate.format(DATE_FORMAT),
                new: formattedIssuanceDate.format(DATE_FORMAT),
              },
              cost: {
                old: cost,
                new: generalCost.cost,
              },
            });
            refetch();
          },
        });
      },
    });
  };

  const recalculateCost = ({
    cost,
    issuanceDate,
    oldIssuanceDate,
    setFieldValue,
    setShowChangeCostModal,
    isCancel,
    isRia,
  }) => {
    return setShowChangeCostModal({
      isRia,
      issuanceDate: {
        old: oldIssuanceDate || moment().format("DD/MM/YYYY"),
        new: issuanceDate || moment().add(10, "days").format("DD/MM/YYYY"),
      },
      cost: {
        old: cost || 100,
        new: 120,
      },
    });
  };

  useQuery(INSURANCE_PLAN, {
    variables: {
      where: {
        id: insurancePlanId,
      },
    },
    onCompleted: ({ insurancePlan }) => {
      const deltaStartRia =
        max(
          findRules({
            params: get(insurancePlan, "params"),
            ruleName: INSURANCE_PLAN_PARAMS_RULES.DELTA_START_RIA,
          }),
        ) || DEFAULT_DELTA_START_RIA;
      const deltaStart =
        max(
          findRules({
            params: get(insurancePlan, "params"),
            ruleName: INSURANCE_PLAN_PARAMS_RULES.DELTA_START,
          }),
        ) || DEFAULT_DELTA_START;
      const deltaEnd =
        max(
          findRules({
            params: get(insurancePlan, "params"),
            ruleName: INSURANCE_PLAN_PARAMS_RULES.DELTA_END,
          }),
        ) || DEFAULT_DELTA_END;
      const deltaEndRia =
        max(
          findRules({
            params: get(insurancePlan, "params"),
            ruleName: INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA,
          }),
        ) || DEFAULT_DELTA_END_RIA;
      setDeltas({
        start: { normal: deltaStart, ria: deltaStartRia },
        end: { normal: deltaEnd, ria: deltaEndRia },
      });
    },
  });

  const onSubmit = ({ contract, ria, fields, terminationRequest }) => {
    setLoading(true);
    const source = localStorage.getItem(KEYS.SOURCE);
    updateUrl({
      subscriptionId,
      url: get(
        data,
        "subscription.insurancePlan.config.generatedUrl",
        `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
      ),
    });
    onNext({
      payload: {
        contact: {
          ...get(project, "contact"),
          acceptContact: true,
          acceptDigital: true,
        },
        contract: {
          ...contract,
          caseType: ria
            ? CASE_TYPES.RETURN_TO_COMPETITION
            : CASE_TYPES.NEW_CONTRACT,
        },
        ria,
        fields,
        terminationRequest,
      },
      onCompleted: () => setLoading(false),
    });
  };

  const [getGeneralCost, { data: generalCost, refetch }] = useLazyQuery(
    graphql.queries.GENERAL_COST,
    {
      variables: {
        where: {
          project: { id: get(project, "id") },
          insurancePlan: {
            id: get(data, "subscription.insurancePlan.id"),
          },
        },
      },
    },
  );

  if (loading) return <Loader />;

  return (
    <>
      <Navbar
        project={project}
        progress={currentStatus?.progress}
        tracer={tracer}
        showCart
      />
      <View
        contract={get(project, "contract", {})}
        fields={get(project, "fields", {})}
        terminationRequest={get(project, "terminationRequest", {})}
        ria={project?.ria}
        locked={project?.locked}
        deltas={deltas}
        onSubmit={onSubmit}
        onBack={onBack}
        recalculateCost={recalculateCost}
        generalCost={get(generalCost, "generalCost")}
        onProductEdit={onProductEdit}
        subscription={get(data, "subscription")}
        initialValues={initialValues}
        isCollective={!!get(project, "fields.branch")}
      />
    </>
  );
};

export default EffectiveDate;
